// ===== Layer 3: 검색 유틸리티 (의존성: base.ts, categories.ts, categoryUtils.ts) =====

import type { SearchableDto, CodeType, ItemSearchFilters, CustomerSearchFilters, EquipmentPopupSearchFilters, InspectPopupSearchFilters, WorkPlacePopupSearchFilters, WarehousePopupSearchFilters } from 'src/types/base';
import { getSearchFieldsByCodeType } from './categoryUtils';
import { isCustomer, isEmployee, isItem, isMenu, isMenuAuth, isOrg, isUser, isInspect, isEquipment, isWorkplace, isWarehouse, isCompany } from 'src/components/common/modal/codeSearchModal/SearchTableContent';



// ===== 타입 가드 유틸리티 =====

/**
 * 아이템의 타입을 판단하는 헬퍼 함수
 */
const getItemType = (item: SearchableDto): CodeType | null => {
  if (isItem(item)) return 'item';
  if (isCustomer(item)) return 'customer';
  if (isOrg(item)) return 'organization';
  if (isEmployee(item)) return 'employee';
  if (isUser(item)) return 'user';
  if (isMenu(item)) return 'menu';
  if (isMenuAuth(item)) return 'menuAuth';
  if (isInspect(item)) return 'inspect';
  if (isEquipment(item)) return 'equipment';
  if (isWorkplace(item)) return 'workplace';
  if (isWarehouse(item)) return 'warehouse';
  if (isCompany(item)) return 'company';
  return null;  
};

// ===== 클라이언트 사이드 필터링 =====

/**
 * 클라이언트 사이드 필터링 함수
 */
export const filterSearchableData = (
  data: SearchableDto[], 
  searchTerm: string, 
  category?: CodeType
): SearchableDto[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }

  const searchLower = searchTerm.toLowerCase();

  return data.filter(item => {
    // 카테고리가 지정된 경우 해당 카테고리의 필드만 검색
    if (category) {
      const fields = getSearchFieldsByCodeType(category);
      return fields.some((field: string) => {
        const value = (item as any)[field];
        return value && value.toString().toLowerCase().includes(searchLower);
      });
    }

    // 카테고리가 지정되지 않은 경우 타입 가드로 판단
    const itemType = getItemType(item);
    if (itemType) {
      const fields = getSearchFieldsByCodeType(itemType);
      return fields.some((field: string) => {
        const value = (item as any)[field];
        return value && value.toString().toLowerCase().includes(searchLower);
      });
    }
    
    return false;
  });
};

// ===== 필터 유효성 검사 =====

/**
 * 품목 검색 필터 유효성 검사
 */
export const validateItemSearchFilters = (filters: Partial<ItemSearchFilters>): ItemSearchFilters => {
  return {
    itemAccount: filters.itemAccount || 'all',
    itemGroup: filters.itemGroup || 'all',
    brandCd: filters.brandCd || 'all'
  };
};

/**
 * 거래처 검색 필터 유효성 검사
 */
export const validateCustomerSearchFilters = (filters: Partial<CustomerSearchFilters>): CustomerSearchFilters => {
  return {
    areaCd: filters.areaCd || 'all'
  };
};

/**
 * 설비 검색 필터 유효성 검사
 */
export const validateEquipmentSearchFilters = (filters: Partial<EquipmentPopupSearchFilters>): EquipmentPopupSearchFilters => {
  return {
    equipmentNm: filters.equipmentNm || 'all',
    equipmentCd: filters.equipmentCd || 'all'
  };
};

/**
 * 설비점검코드 검색 필터 유효성 검사
 */
export const validateInspectSearchFilters = (filters: Partial<InspectPopupSearchFilters>): InspectPopupSearchFilters => {
  return {
    inspectMethod: filters.inspectMethod || 'all',
    inspectNm: filters.inspectNm || 'all',
    inspectType: filters.inspectType || 'all',
    eqmType: filters.eqmType || 'all'
  };
};

/**
 * 공정코드 검색 필터 유효성 검사
 */
export const validateWorkPlaceSearchFilters = (filters: Partial<WorkPlacePopupSearchFilters>): WorkPlacePopupSearchFilters => {
  return {
    workplaceNm: filters.workplaceNm || '',
    workplaceCd: filters.workplaceCd || ''
  };
};

/**
 * 창고코드 검색 필터 유효성 검사
 */
export const validateWarehouseSearchFilters = (filters: Partial<WarehousePopupSearchFilters>): WarehousePopupSearchFilters => {
  return {
    warehouseNm: filters.warehouseNm || '',
    warehouseCd: filters.warehouseCd || ''
  };
};


